/**
 * M8 Deep Analysis — client-safe public surface. The spec contract, the pure
 * spatial-compute foundation, the per-tool builders (radius / territories /
 * boundary), the heavy-compute job seam, and the gating logic. Everything here
 * is browser-safe and unit-tested; the analysis overlay renderer + tool UI live
 * beside the M4 renderer under src/app/(editor)/map. See docs/build/M8.md.
 */

// Spec contract (also re-exported from @/lib/map for renderer convenience).
export {
  ANALYSIS_VERSION,
  AnalysisSpecSchema,
  emptyAnalysis,
  parseAnalysis,
  migrateAnalysis,
  type AnalysisSpec,
  type Territories,
  type Territory,
  type TerritoryMode,
  type TerritoryRollup,
  type BoundaryKind,
  type BoundaryRef,
  type RadiusCircle,
  type RadiusCenter,
  type DriveTime,
  type Routes,
  type LngLat,
  type Ring as SpecRing,
} from "./spec";

// Spatial-compute foundation.
export {
  pointInRing,
  pointsInRing,
  pointsInRadius,
  haversineMeters,
  circleRing,
  centroid,
  ringCentroid,
  bboxOf,
  convexHull,
  MILES_TO_METERS,
  type Ring,
} from "./geometry";
export { balancedPartition, type PartitionOptions, type PartitionResult } from "./partition";

// Heavy-compute job seam (worker/server envelope).
export {
  createInlineJobRunner,
  createWorkerJobRunner,
  runJobInline,
  toJobPoints,
  HEAVY_JOB_THRESHOLD,
  type AnalysisJobRequest,
  type AnalysisJobResponse,
  type AnalysisJobRunner,
  type JobPoint,
} from "./job";

// Off-thread worker wiring (M8-B closeout) — pure handler + deferred entry seam.
export {
  handleAnalysisJobMessage,
  installAnalysisWorker,
  createAnalysisWorkerRunner,
  type AnalysisWorkerScope,
} from "./worker";

// Per-tool builders.
export {
  buildRadiusCircle,
  ringsAroundPoint,
  ringsAroundPoints,
  recomputeRadius,
  resolveCenter,
  radiusLabel,
  unionInside,
  metersBetween,
  isInsideCircle,
} from "./radius";
export {
  buildLassoTerritory,
  buildAutomatedTerritories,
  recomputeLassoTerritory,
  territoryColor,
  rollupOf,
  type AutomatedOptions,
} from "./territories";
export {
  assignToBoundaries,
  assignViaProvider,
  createFixtureBoundaryProvider,
  type BoundaryPolygon,
  type BoundaryProvider,
} from "./boundary";

// Route optimization (M8-B) — provider-metered, OSS-first haversine by default.
export {
  buildRouteOptimization,
  recomputeRoute,
  resolveStops,
  routeTotalsLabel,
  routeExportRows,
  routeExportCsv,
  MAX_ROUTE_STOPS,
  type RouteBuildResult,
  type RouteExportRow,
} from "./routes";

// Drive-time isochrones (M8-B) — provider-metered, labeled stub by default.
export {
  buildDriveTime,
  resolveDriveTimeOrigin,
  driveTimeBandLabel,
  DRIVE_TIME_FILL_OPACITY,
  type DriveTimeOriginRefs,
  type DriveTimeBuildResult,
} from "./driveTime";

// Analysis-intent engine + apply/preview hooks (M8-B NL + share/embed).
export { buildAnalysisFromIntent, type AnalysisIntent, type AnalysisIntentDeps } from "./intent";
export {
  applyAnalysisSpec,
  isEmptyAnalysis,
  highlightTerritory,
  previewOverlay,
  type PreviewOverlay,
  type PreviewInput,
} from "./apply";

// Gating (build-go + M7 entitlement).
export {
  analysisToolGate,
  analysisToolGates,
  anyToolPresent,
  isAnalysisEntitled,
  type AnalysisEntitlement,
  type AnalysisToolGate,
  type AnalysisToolBlock,
} from "./gate";
